import { useEffect, useState } from 'react';
import { API_ROOT } from '../config';

/**
 * One person, as a circle — the ONE implementation, used by the presence stack in every
 * screen's header, by the badges on a node, by the document overlay, and by the admin users
 * table. It lived inside `PresenceStack.tsx` until avatars existed; a second copy anywhere is
 * the failure mode this codebase has already paid for once.
 *
 * INITIALS ARE STILL THE DEFAULT, and the geometry is unchanged. The markup is the same
 * `<span class="avatar">` it has always been, with the same colour, the same `.editing` ring
 * and the same `.more` variant — an `<img>` is only ever placed INSIDE it, so an installation
 * where nobody has uploaded a picture looks byte-for-byte like the one before this feature.
 *
 * THE VERSION IS THE CACHE KEY. `avatarV` (the server's `avatarUpdatedAt` in epoch millis)
 * comes down on the presence roster and on `/api/auth/me`; the URL it builds carries it as
 * `?v=`, so a changed picture is a different URL and the browser's own cache does the
 * invalidating. Nothing about an avatar's BYTES is ever in a JSON payload.
 *
 * `onError` FALLS BACK TO THE INITIALS, and that is not defensive noise: an avatar deleted
 * while somebody's tab is open leaves a version pointing at a 404, and a broken-image glyph in
 * a header is worse than the initials that were there yesterday. The reset on `src` is what
 * makes the fallback survive a later upload.
 */

/** Anything that can be drawn as an avatar. `PresenceUser` satisfies it structurally. */
export interface AvatarSubject {
  userId: string;
  initials?: string;
  color?: string;
  /** The picture's version, or null/undefined when this account has none. */
  avatarV?: number | null;
}

/**
 * Where one account's picture lives. Absolute from the origin root like every other API URL
 * (`API_ROOT`) and deliberately NOT under `BASE_PATH` — the Express routes are mounted at
 * `/api` wherever the static app is served from.
 */
export function avatarUrl(userId: string, avatarV: number): string {
  return `${API_ROOT}/users/${encodeURIComponent(userId)}/avatar?v=${avatarV}`;
}

/** The square an uploaded picture is reduced to before it is sent. */
export const AVATAR_PX = 128;

/**
 * Turn whatever somebody picked into an avatar-sized image, in the browser.
 *
 * THE DOWNSCALE IS THE MECHANISM; the server's 512 KB cap is the backstop. A phone photograph
 * is several megabytes and would be refused — sending it and reading the refusal would be a
 * worse experience than simply never sending it. Cover-crop from the centre, because the
 * circle it lands in crops anyway and letterboxing a portrait inside it looks broken.
 *
 * WebP, falling back to PNG: `toBlob` answers `null` (or an empty blob) for a type a browser
 * cannot encode, and a silent zero-byte upload would store an avatar that renders as nothing.
 * Lives beside `avatarUrl()` deliberately — how an avatar is made and where it lives are one
 * concept, and there is exactly one of each.
 */
export async function downscaleToAvatar(file: File): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  try {
    const side = Math.min(bitmap.width, bitmap.height);
    const canvas = document.createElement('canvas');
    canvas.width = AVATAR_PX;
    canvas.height = AVATAR_PX;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('This browser could not process the image.');
    ctx.drawImage(
      bitmap,
      (bitmap.width - side) / 2,
      (bitmap.height - side) / 2,
      side,
      side,
      0,
      0,
      AVATAR_PX,
      AVATAR_PX,
    );
    const webp = await canvasBlob(canvas, 'image/webp');
    if (webp && webp.size) return webp;
    const png = await canvasBlob(canvas, 'image/png');
    if (png && png.size) return png;
    throw new Error('This browser could not encode the image.');
  } finally {
    bitmap.close?.();
  }
}

function canvasBlob(canvas: HTMLCanvasElement, type: string): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, 0.9));
}

export function Avatar({
  user,
  editing,
  title,
  onClick,
}: {
  user: AvatarSubject;
  editing?: boolean;
  title?: string;
  /** Plan 1.7: clicking a colleague's avatar jumps to where they are. Additive — same markup. */
  onClick?: () => void;
}) {
  const src = user.avatarV ? avatarUrl(user.userId, user.avatarV) : '';
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);

  return (
    <span
      className={editing ? 'avatar editing' : 'avatar'}
      style={{ background: user.color || 'var(--accent)' }}
      title={title}
      onClick={onClick}
    >
      {src && !failed ? (
        <img
          className="avatar-img"
          src={src}
          // The name is already on the enclosing span's `title`; repeating it here would make
          // a screen reader say it twice for one person.
          alt=""
          loading="lazy"
          draggable={false}
          onError={() => setFailed(true)}
        />
      ) : (
        user.initials || '?'
      )}
    </span>
  );
}
